import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../../components/layout/Header/Header";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import DirhamSymbol from "../../../Assets/aed-symbol.png";
import toast from "../../../utils/toast";
import axios from "axios";
import TableScrollSync from "../../../components/common/TableScrollSync";
import "./StorekeeperOrders.css";
import InvoiceDownloadModal from "../../../components/InvoiceDownloadModal/InvoiceDownloadModal";
import { useAppSettings } from "../../../context/AppSettingsContext";
import { usePaginatedData } from "../../../hooks/usePagination";
import Pagination from "../../../components/common/Pagination";
import OrderProductsModal from "../../../components/common/OrderProductsModal";

const StorekeeperOrders = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState("All Orders");
  const [user, setUser] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [pendingInvoiceData, setPendingInvoiceData] = useState(null);
  const [viewProductsOrder, setViewProductsOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "not_packed" | "partially_packed" | "fully_packed"
  const [invoiceFilter, setInvoiceFilter] = useState("");
  const [nextInvoicePreview, setNextInvoicePreview] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_IP;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        window.location.href = "/login";
        return;
      }
      const response = await axios.get(`${backendUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data.user || response.data);
    } catch (error) {
      console.error("Failed to load user", error);
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
  }, [backendUrl]);

  const fetchAllOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${backendUrl}/api/orders/all-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  const fetchNextInvoicePreview = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${backendUrl}/api/orders/delivery-invoice-status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNextInvoicePreview(response.data);
    } catch (error) {
      console.error("Error fetching next invoice preview:", error);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchCurrentUser();
    fetchAllOrders();
    fetchNextInvoicePreview();
  }, [fetchCurrentUser, fetchAllOrders, fetchNextInvoicePreview]);

  // ✅ Download invoice with correct filename
  const downloadUnifiedInvoice = async (orderId, invoiceNumber, type = "normal") => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${backendUrl}/api/orders/unified-invoice/${orderId}?invoiceNumber=${invoiceNumber}&type=${type}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      // Open the print dialog instead of downloading
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
      toast.success("Opening print dialog for invoice");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download invoice");
    }
  };

  const { entriesPerPage, invoiceSystemActive } = useAppSettings();

  // ✅ Filter orders: Show only packed orders (like PackOrders page)
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // Search filter
      const matchesSearch =
        !searchTerm.trim() ||
        order.customer?.name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order._id?.toLowerCase().includes(searchTerm.toLowerCase());

      // Invoice filter
      const matchesInvoice =
        !invoiceFilter.trim() ||
        order.invoiceNumber
          ?.toLowerCase()
          .includes(invoiceFilter.toLowerCase());

      // ✅ Packing status filter - Show only packed orders (partially or fully)
      const matchesStatus =
        statusFilter === "all"
          ? order.packedStatus !== "not_packed" // Exclude not_packed by default
          : order.packedStatus?.toLowerCase() === statusFilter.toLowerCase();

      // Once InvoiceCounter is deleted, orders that already have a generated
      // invoice are dropped entirely rather than just hiding their invoice number.
      const hasInvoice =
        !!order.invoiceNumber ||
        !!order.deliveredInvoiceNumber ||
        (order.deliveredInvoiceHistory && order.deliveredInvoiceHistory.length > 0);
      const matchesInvoiceVisibility = invoiceSystemActive || !hasInvoice;

      return matchesSearch && matchesInvoice && matchesStatus && matchesInvoiceVisibility;
    });
  }, [orders, searchTerm, invoiceFilter, statusFilter, invoiceSystemActive]);
  const pagination = usePaginatedData(
    filteredOrders,
    entriesPerPage,
    `${searchTerm}|${invoiceFilter}|${statusFilter}`
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getDeliveryStatusBadge = (order) => {
    const status = order.status || "pending";
    const map = {
      pending: { label: "Pending", class: "status-not-packed" },
      ready_to_deliver: { label: "Ready to Deliver", class: "status-ready" },
      partial_delivered: { label: "Partially Delivered", class: "status-partial" },
      delivered: { label: "Fully Delivered", class: "status-packed" },
      cancelled: { label: "Cancelled", class: "status-cancelled" },
    };
    return map[status] || { label: status, class: "status-neutral" };
  };

  if (!user) return <div className="order-list-loading">Loading...</div>;

  return (
    <div className="order-list-layout">
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        user={user}
      />
      <Sidebar
        isOpen={sidebarOpen}
        activeItem={activeItem}
        onSetActiveItem={setActiveItem}
        onClose={() => setSidebarOpen(false)}
        user={user}
      />
      <main
        className={`order-list-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
      >
        <div className="order-list-container-wrapper">
          <div className="order-list-container">
            <div className="order-list-header-section">
              <h2 className="order-list-page-title">All Orders</h2>
              <p className="sub">
                Complete order history with packing & delivery status
              </p>
              {nextInvoicePreview && (
                <p className="sub" style={{ marginTop: 4 }}>
                  Next Invoice #: <strong>{nextInvoicePreview.nextInvoiceNumber}</strong>
                </p>
              )}
            </div>

            {/* Controls */}
            <div className="order-list-controls-group">
              <div className="order-list-search-container">
                <input
                  type="text"
                  className="order-list-search-input"
                  placeholder="Search by customer or order ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="order-list-search-clear"
                    onClick={() => setSearchTerm("")}
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="order-list-search-container">
                <input
                  type="text"
                  className="order-list-search-input"
                  placeholder="Filter by Invoice # (DDFT/101001/26)..."
                  value={invoiceFilter}
                  onChange={(e) => setInvoiceFilter(e.target.value)}
                />
                {invoiceFilter && (
                  <button
                    className="order-list-search-clear"
                    onClick={() => setInvoiceFilter("")}
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="order-list-filter-group">
                <label
                  htmlFor="statusFilter"
                  className="order-list-filter-label"
                >
                  Packing Status:
                </label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="order-list-filter-select"
                >
                  <option value="all">All Packed</option>
                  <option value="partially_packed">Partially Packed</option>
                  <option value="fully_packed">Fully Packed</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="order-list-loading">Loading orders...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="order-list-no-data">No packed orders found</div>
            ) : (
              <>
                <TableScrollSync>
                  <div className="order-list-table-wrapper">
                    <table className="order-list-data-table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Packed Qty</th>{" "}
                          {/* ✅ Show packed qty (not total ordered) */}
                          <th>Delivered</th>
                          <th>Amount</th> {/* ✅ Show amount for packed qty */}
                          <th>Status</th>
                          <th>Order Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.pageData.map((order, index) => {
                          const totalPacked =
                            order.orderItems?.reduce(
                              (s, i) => s + (i.packedQuantity || 0),
                              0,
                            ) || 0;
                          const totalDelivered =
                            order.orderItems?.reduce(
                              (s, i) => s + i.deliveredQuantity,
                              0,
                            ) || 0;

                          // ✅ Calculate amount based on packed quantity (VAT-inclusive)
                          const packedAmount =
                            order.orderItems
                              ?.reduce((sum, item) => {
                                const packedQty = item.packedQuantity || 0;
                                const previouslyInvoiced =
                                  item.invoicedQuantity || 0;
                                const qtyForThisInvoice =
                                  packedQty - previouslyInvoiced;
                                // Use totalAmount (incl. VAT) proportionally
                                const ratio = item.orderedQuantity > 0
                                  ? qtyForThisInvoice / item.orderedQuantity
                                  : 0;
                                const itemTotal = item.totalAmount
                                  ? item.totalAmount * ratio
                                  : qtyForThisInvoice * item.price * (1 + (item.vatPercentage || 5) / 100);
                                return sum + itemTotal;
                              }, 0)
                              .toFixed(2) || "0.00";

                          const statusBadge = getDeliveryStatusBadge(order);

                          return (
                            <tr key={order._id}>
                              <td>{pagination.showingFrom + index}</td>
                              <td>
                                <button
                                  type="button"
                                  className="order-list-orderid-link"
                                   onClick={() => setViewProductsOrder(order)}
                                   title="View packed products"
                                >
                                  {order.orderId || order._id}
                                </button>
                              </td>

                              <td>
                                <span className="customer-name">
                                  {order.customer?.name || "N/A"}
                                </span>
                              </td>

                              {/* ✅ Show PACKED quantity (not ordered) */}
                              <td className="qty-cell packed">{totalPacked}</td>

                              <td className="qty-cell delivered">
                                {totalDelivered}
                              </td>

                              {/* ✅ Show amount for packed qty */}
                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <img
                                    src={DirhamSymbol}
                                    alt="AED"
                                    width={14}
                                    height={14}
                                  />
                                  <span>{packedAmount}</span>
                                </div>
                              </td>

                              <td>
                                <span
                                  className={`status-badge ${statusBadge.class}`}
                                >
                                  {statusBadge.label}
                                </span>
                              </td>

                              <td>{formatDate(order.orderDate)}</td>

                              <td>
                                {order.packedStatus &&
                                  order.packedStatus !== "not_packed" &&
                                  order.invoiceHistory &&
                                  order.invoiceHistory.length > 0 ? (
                                    <div className="invoice-buttons-group">
                                      {order.invoiceHistory.map((inv, i) => (
                                        <button
                                          key={i}
                                          className="order-list-icon-button order-list-view-button invoice-btn"
                                          onClick={() => {
                                            setPendingInvoiceData({ orderId: order._id, invoiceNumber: inv.invoiceNumber });
                                            setShowInvoiceModal(true);
                                          }}
                                          title={`Download ${inv.invoiceNumber}`}
                                        >
                                          📄 {inv.invoiceNumber}
                                        </button>
                                      ))}
                                    </div>
                                  ) : order.packedStatus &&
                                    order.packedStatus !== "not_packed" &&
                                    order.invoiceNumber ? (
                                    <button
                                      className="order-list-icon-button order-list-view-button"
                                      onClick={() => {
                                        setPendingInvoiceData({ orderId: order._id, invoiceNumber: order.invoiceNumber });
                                        setShowInvoiceModal(true);
                                      }}
                                      title={`Download invoice ${order.invoiceNumber}`}
                                    >
                                      📄 Invoice
                                    </button>
                                  ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </TableScrollSync>

                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalRecords={pagination.totalRecords}
                  showingFrom={pagination.showingFrom}
                  showingTo={pagination.showingTo}
                  canPrev={pagination.canPrev}
                  canNext={pagination.canNext}
                  onPrev={pagination.goPrev}
                  onNext={pagination.goNext}
                />
              </>
            )}
          </div>
        </div>
      </main>

      <InvoiceDownloadModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        onSelect={(type) => {
          setShowInvoiceModal(false);
          if (pendingInvoiceData) {
            downloadUnifiedInvoice(pendingInvoiceData.orderId, pendingInvoiceData.invoiceNumber, type);
          }
        }}
      />

      {viewProductsOrder && (
        <OrderProductsModal
          order={viewProductsOrder}
          onClose={() => setViewProductsOrder(null)}
          filterItem={(item) => (item.packedQuantity || 0) > 0}
          getQty={(item) => item.packedQuantity || 0}
          emptyText="No packed products"
        />
      )}
    </div>
  );
};

export default StorekeeperOrders;
